import { NotificationService } from '@ui/notification/notification.service';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import {
  ComplaintAttachment,
  ComplaintRecord,
} from '@api/models/assignment.model';
import { AssignmentsService } from '@api/services/assignments.service';
import { IconComponent } from '@ui/icon/icon.component';
import { ModalComponent } from '@ui/modal/modal.component';
import { MODAL_REF, ModalRef, ModalService } from '@ui/modal/modal.service';
import { LoadingService } from '@ui/app-layout/loading-service';

@Component({
  selector: 'app-complaint-modal',
  standalone: true,
  imports: [ModalComponent, CommonModule, IconComponent],
  template: `
    <ui-modal
      [title]="item?.subject ?? 'Klacht'"
      [submitText]="'Sluiten'"
      [hideCancel]="true"
      (save)="closeModal()"
      (close)="closeModal()"
      [size]="'xl'"
    >
      <div class="space-y-4">
        <div class="grid grid-cols-[120px_1fr] gap-y-3 text-sm">
          <span class="text-gray-600">Klachtnummer:</span>
          <span class="font-medium">{{ item?.dossier_item ?? '—' }}</span>

          <span class="text-gray-600">Ingediend:</span>
          <span class="font-medium">{{
            item?.instuur_datum | date : 'dd-MM-yyyy'
          }}</span>

          <span class="text-gray-600">Status:</span>
          <span class="font-medium">{{ item?.status ?? '—' }}</span>

          <span class="text-gray-600">Onderwerp:</span>
          <span class="font-medium">{{ item?.subject ?? '—' }}</span>

          <span class="text-gray-600">Klacht opgelost:</span>
          <span class="font-medium">{{
            item?.klacht_opgelost === null
              ? '—'
              : item?.klacht_opgelost
                ? 'Ja'
                : 'Nee'
          }}</span>

          <span class="text-gray-600">Klacht gegrond:</span>
          <span class="font-medium">{{
            item?.klacht_gegrond === null
              ? '—'
              : item?.klacht_gegrond
                ? 'Ja'
                : 'Nee'
          }}</span>

          @if (item?.workflow_afgerond) {
            <span class="text-gray-600">Afgerond:</span>
            <span class="font-medium">{{
              item?.workflow_afgerond | date : 'dd-MM-yyyy'
            }}</span>
          }
        </div>

        <div>
          <div class="grid grid-cols-1 gap-2 mt-2">
            @if (item?.attachments?.length) {
              <span class="text-gray-600 text-sm">Bijlagen:</span>
            }
            @for (attachment of item?.attachments; track attachment.file_id) {
              @if (attachment.file_id && attachment.subject_id) {
                <div
                  class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <div class="flex items-center space-x-3">
                    <ui-icon
                      [name]="getFileIcon(attachment.name ?? '')"
                      class="w-5 h-5 text-gray-500"
                    />
                    <span class="text-sm font-medium">{{
                      attachment.name
                    }}</span>
                  </div>
                  <button
                    (click)="downloadFile(attachment)"
                    class="p-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <ui-icon name="download" class="w-5 h-5" />
                  </button>
                </div>
              }
            } @empty {
              <div class="text-center text-gray-500">Geen bijlagen gevonden</div>
            }
          </div>
        </div>
      </div>
    </ui-modal>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComplaintModalComponent {
  @Input() item: ComplaintRecord | null = null;
  @Input() assignmentId: string | null = null;

  private modalService = inject(ModalService);
  private modalRef = inject(MODAL_REF);
  private assignmentsService = inject(AssignmentsService);
  private loadingService = inject(LoadingService);
  private notificationService = inject(NotificationService);

  closeModal() {
    this.modalService.close(this.modalRef as ModalRef<any>);
  }

  downloadFile(attachment: ComplaintAttachment) {
    if (
      !this.assignmentId ||
      attachment.subject_id == null ||
      !attachment.file_id
    ) {
      return;
    }

    this.loadingService.show();

    this.assignmentsService
      .downloadFile(
        this.assignmentId,
        attachment.subject_id.toString(),
        attachment.file_id
      )
      .subscribe({
        next: (response: ArrayBuffer) => {
          const blob = new Blob([response], {
            type: this.getContentType(attachment.name ?? ''),
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = attachment.name ?? 'bijlage';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.loadingService.hide();
          this.notificationService.success('Bijlage gedownload');
        },
        error: () => {
          this.loadingService.hide();
          this.notificationService.error(
            'Fout bij het downloaden van de bijlage'
          );
        },
      });
  }

  private getContentType(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase() ?? '';

    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xls':
        return 'application/vnd.ms-excel';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'zip':
        return 'application/zip';
      case 'rar':
        return 'application/x-rar-compressed';
      default:
        return 'application/octet-stream';
    }
  }

  getFileIcon(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase() ?? '';

    switch (extension) {
      case 'pdf':
        return 'file-pdf';
      case 'doc':
      case 'docx':
        return 'file-word';
      case 'xls':
      case 'xlsx':
        return 'file-excel';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return 'file-image';
      case 'zip':
      case 'rar':
      case '7z':
        return 'file-archive';
      default:
        return 'file';
    }
  }
}
